import { useEffect, useState } from "react";

// Collection/delivery charges. The backend mirrors this in app/Services/BookingChargeCalculator.php
// and recomputes the amounts on save, so keep the two in sync.

export const CHARGE_SERVICES = ["collection", "delivery"];

export const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));
export const gbp = (v) => `£${Number(v || 0).toFixed(2)}`;
// Decimal-string shift avoids float error (19.025 → 19.03), matching PHP's round() on the backend
const round2 = (v) => Math.round(Number(`${num(v).toFixed(10)}e2`)) / 100;

// Base charge covers the first `base_miles`, each extra mile is billed at `rate_per_mile`,
// and the result never drops below `minimum_charge`.
export const calculateCharge = (charge, miles) => {
  if (!charge?.enabled) return { total: 0, breakdown: "Charge is turned off." };

  if (charge.type === "fixed") {
    return { total: round2(charge.fixed_amount), breakdown: "Flat rate, regardless of distance." };
  }

  const baseMiles = num(charge.base_miles);
  const extraMiles = round2(Math.max(0, num(miles) - baseMiles));
  const subtotal = num(charge.base_charge) + extraMiles * num(charge.rate_per_mile);
  const minimum = num(charge.minimum_charge);
  const total = round2(Math.max(subtotal, minimum));

  let breakdown = `${gbp(charge.base_charge)} base`;
  if (baseMiles > 0) breakdown += ` (covers first ${baseMiles} mi)`;
  breakdown += ` + ${extraMiles} mi × ${gbp(charge.rate_per_mile)}`;
  if (minimum > subtotal) breakdown += ` → raised to minimum ${gbp(minimum)}`;

  return { total, breakdown };
};

// True when the company has at least one charge switched on; all charge UI is hidden otherwise
export const hasCharges = (charges) => !!(charges?.collection?.enabled || charges?.delivery?.enabled);

export const resolveEndPoint = (charge) => {
  const sameEnd = charge?.same_end_point === undefined || !!charge?.same_end_point;
  return sameEnd || !charge?.end_point?.address ? charge?.start_point : charge?.end_point;
};

const toLocation = (point) => {
  const lat = parseFloat(point?.latitude);
  const lng = parseFloat(point?.longitude);
  if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
  return point?.address || null;
};

// The Maps JS API is normally already on the page (loaded by the address autocomplete in the
// pickup/recipient steps). Load it ourselves only if it isn't.
const loadGoogleMaps = () => new Promise((resolve, reject) => {
  if (window.google?.maps?.DirectionsService) return resolve(window.google.maps);

  if (!document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_MAP_API_KEY}&libraries=places`;
    script.async = true;
    document.head.appendChild(script);
  }

  let tries = 0;
  const timer = setInterval(() => {
    if (window.google?.maps?.DirectionsService) {
      clearInterval(timer);
      resolve(window.google.maps);
    } else if (++tries > 100) {
      clearInterval(timer);
      reject(new Error("Google Maps could not be loaded."));
    }
  }, 100);
});

// The pickup/recipient steps and the summary ask for the same routes, so remember them
const routeCache = new Map();

// Driving distance in miles for start → via → end
export const getRouteMiles = async (start, via, end) => {
  const origin = toLocation(start);
  const stop = toLocation(via);
  const destination = toLocation(end);
  if (!origin || !stop || !destination) throw new Error("Missing address to calculate the distance.");

  const cacheKey = JSON.stringify([origin, stop, destination]);
  if (routeCache.has(cacheKey)) return routeCache.get(cacheKey);

  const maps = await loadGoogleMaps();
  const result = await new maps.DirectionsService().route({
    origin,
    destination,
    waypoints: [{ location: stop, stopover: true }],
    travelMode: maps.TravelMode.DRIVING,
  });
  const metres = (result?.routes?.[0]?.legs || []).reduce((sum, leg) => sum + (leg?.distance?.value || 0), 0);
  const miles = round2(metres / 1609.344);
  routeCache.set(cacheKey, miles);
  return miles;
};

const emptyService = { enabled: false, type: null, miles: null, amount: 0 };
export const emptyBookingCharges = { collection: emptyService, delivery: emptyService, total: 0 };

const SERVICE_LABELS = { collection: "collection", delivery: "delivery" };

// One service's charge; per-mile distance is Start → `via` (pickup or recipient address) → End
export const computeServiceCharge = async (charge, via, service = "collection") => {
  if (!charge?.enabled) return emptyService;

  let miles = null;
  if (charge.type === "mile") {
    try {
      miles = await getRouteMiles(charge.start_point, via, resolveEndPoint(charge));
    } catch (e) {
      throw new Error(`Could not calculate the ${SERVICE_LABELS[service] || service} distance. Please check the address and try again.`);
    }
  }
  const { total } = calculateCharge(charge, miles);
  return { enabled: true, type: charge.type, miles, amount: total };
};

// Collection is measured from the pickup address, delivery from the recipient address
export const computeBookingCharges = async (charges, pickUpData, recipientData) => {
  const vias = { collection: pickUpData, delivery: recipientData };
  const result = { total: 0 };

  for (const service of CHARGE_SERVICES) {
    result[service] = await computeServiceCharge(charges?.[service], vias[service], service);
    result.total = round2(result.total + result[service].amount);
  }

  return result;
};

const hasCoordinates = (point) => !isNaN(parseFloat(point?.latitude)) && !isNaN(parseFloat(point?.longitude));

// Live charge for a single address while it's being picked (pickup/recipient steps).
// Idles when the charge is off, or for per-mile charges until the address has coordinates.
export const useServiceCharge = (charge, via, service) => {
  const [state, setState] = useState({ loading: false, error: "", data: null });
  const needsRoute = charge?.enabled && charge?.type === "mile";
  const ready = charge?.enabled && (!needsRoute || hasCoordinates(via));
  const key = JSON.stringify([charge, ready ? [via?.latitude, via?.longitude] : null]);

  useEffect(() => {
    let active = true;
    if (!ready) {
      setState({ loading: false, error: "", data: null });
      return () => { active = false; };
    }
    setState({ loading: true, error: "", data: null });
    computeServiceCharge(charge, via, service)
      .then((data) => active && setState({ loading: false, error: "", data }))
      .catch((e) => active && setState({ loading: false, error: e.message, data: null }));
    return () => { active = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
};

// `charges` is undefined while the settings are still loading (stays in the loading state)
export const useBookingCharges = (charges, pickUpData, recipientData) => {
  const [state, setState] = useState({ loading: true, error: "", data: emptyBookingCharges });
  const key = JSON.stringify([
    charges === undefined ? "__loading" : charges,
    pickUpData?.address, pickUpData?.latitude, pickUpData?.longitude,
    recipientData?.address, recipientData?.latitude, recipientData?.longitude,
  ]);

  useEffect(() => {
    let active = true;
    setState((s) => ({ ...s, loading: true, error: "" }));
    if (charges === undefined) return () => { active = false; };
    computeBookingCharges(charges, pickUpData, recipientData)
      .then((data) => active && setState({ loading: false, error: "", data }))
      .catch((e) => active && setState({ loading: false, error: e.message, data: emptyBookingCharges }));
    return () => { active = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
};

// Fields sent with a booking; the server recomputes the amounts from the miles
export const chargePayload = (data) => ({
  collection_charge: data?.collection?.amount || 0,
  delivery_charge: data?.delivery?.amount || 0,
  collection_miles: data?.collection?.miles,
  delivery_miles: data?.delivery?.miles,
});
